import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, Users, ShieldCheck, Percent, Download, CheckCircle2, XCircle, BarChart3, Loader2, ClipboardList, Brain, Power } from "lucide-react";

export default function StudeurBackoffice() {
  const [users, setUsers] = useState([]);
  const [kyc, setKyc] = useState([]);
  const [projects, setProjects] = useState([]);
  const [fees, setFees] = useState(null);
  const [stats, setStats] = useState(null);
  const [evalOverview, setEvalOverview] = useState([]);
  const [evalQuestions, setEvalQuestions] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data));
    api.get("/admin/kyc", { params: { status: "pending" } }).then(({ data }) => setKyc(data));
    api.get("/admin/projects").then(({ data }) => setProjects(data));
    api.get("/admin/fees").then(({ data }) => setFees(data));
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/eval/overview").then(({ data }) => setEvalOverview(data)).catch(() => {});
    api.get("/admin/eval/questions").then(({ data }) => setEvalQuestions(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleQuestion = async (qid, active) => {
    await api.post(`/admin/eval/questions/${qid}/toggle?active=${active}`);
    setEvalQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, active } : q)));
    toast.success(active ? "Question activée" : "Question désactivée");
  };

  const reviewKyc = async (id, decision) => {
    await api.post(`/admin/kyc/${id}/review?decision=${decision}`);
    toast.success(decision === "approved" ? "KYC approuvé" : "KYC rejeté"); load();
  };
  const suspend = async (uid, s) => { await api.post(`/admin/users/${uid}/suspend?suspend=${s}`); toast.success("Mis à jour"); load(); };
  const publish = async (id) => { await api.post(`/projects/${id}/status?status=active`); toast.success("Projet publié"); load(); };
  const saveFees = async () => {
    await api.put("/admin/fees", { entry_fee_pct: Number(fees.entry_fee_pct), mgmt_fee_pct: Number(fees.mgmt_fee_pct), interest_commission_pct: Number(fees.interest_commission_pct) });
    toast.success("Frais mis à jour");
  };
  const exportKyc = async () => {
    const { data } = await api.get("/admin/kyc/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "export-kyc-bcc.json"; a.click(); URL.revokeObjectURL(url);
    toast.success(`Export de ${data.count} dossier(s) généré`);
  };

  const drafts = projects.filter((p) => ["draft", "pending_review"].includes(p.status));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-congo-goldlight flex items-center justify-center"><Building2 className="h-6 w-6 text-congo-golddark" /></div>
          <div><h1 className="font-head text-2xl font-bold text-slate-900">Back-office Studeur</h1>
            <p className="text-slate-500 text-sm">Utilisateurs, KYC, contenu, frais & conformité</p></div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-white border rounded-xl p-4"><Users className="h-5 w-5 text-congo-blue" /><p className="text-xs text-slate-500 mt-2">Investisseurs</p><p className="font-head text-xl font-bold">{stats.total_investors}</p></div>
            <div className="bg-white border rounded-xl p-4"><BarChart3 className="h-5 w-5 text-green-600" /><p className="text-xs text-slate-500 mt-2">Taux de conversion</p><p className="font-head text-xl font-bold">{stats.conversion_rate}%</p></div>
            <div className="bg-white border rounded-xl p-4"><ShieldCheck className="h-5 w-5 text-congo-blue" /><p className="text-xs text-slate-500 mt-2">KYC validés</p><p className="font-head text-xl font-bold">{stats.kyc_approved}</p></div>
            <div className="bg-white border rounded-xl p-4"><Percent className="h-5 w-5 text-congo-golddark" /><p className="text-xs text-slate-500 mt-2">Ticket moyen</p><p className="font-head text-xl font-bold">{money(stats.avg_investment)}</p></div>
          </div>
        )}

        <Tabs defaultValue="kyc" className="mt-8">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="kyc" data-testid="tab-kyc">KYC {kyc.length > 0 && <Badge className="ml-1 bg-congo-red text-white border-0">{kyc.length}</Badge>}</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">Contenu</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Utilisateurs</TabsTrigger>
            <TabsTrigger value="fees" data-testid="tab-fees">Frais</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">Conformité</TabsTrigger>
          </TabsList>

          <TabsContent value="kyc" className="pt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-head text-lg font-semibold">Dossiers KYC en attente</h2>
              <Button variant="outline" onClick={exportKyc} data-testid="export-kyc-btn" className="rounded-full"><Download className="h-4 w-4 mr-1" /> Export BCC</Button>
            </div>
            {kyc.length === 0 ? <p className="text-slate-500 text-sm" data-testid="no-kyc">Aucun dossier en attente.</p> : (
              <div className="space-y-3" data-testid="kyc-list">
                {kyc.map((k) => (
                  <div key={k.id} className="bg-white border rounded-xl p-4 flex items-center justify-between gap-3">
                    <div><p className="font-medium">{k.user_name}</p><p className="text-xs text-slate-500">{k.user_email} · {k.doc_type}</p></div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => reviewKyc(k.id, "approved")} data-testid={`approve-${k.id}`} className="rounded-full bg-green-600 hover:bg-green-700"><CheckCircle2 className="h-4 w-4 mr-1" />Approuver</Button>
                      <Button size="sm" variant="outline" onClick={() => reviewKyc(k.id, "rejected")} data-testid={`reject-${k.id}`} className="rounded-full text-congo-red border-congo-red/30"><XCircle className="h-4 w-4 mr-1" />Rejeter</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="content" className="pt-6">
            <h2 className="font-head text-lg font-semibold mb-4">Validation du contenu des projets</h2>
            {drafts.length === 0 ? <p className="text-slate-500 text-sm">Aucun projet à publier.</p> : (
              <div className="space-y-3">
                {drafts.map((p) => (
                  <div key={p.id} className="bg-white border rounded-xl p-4 flex items-center justify-between gap-3">
                    <div><p className="font-medium">{p.title}</p><p className="text-xs text-slate-500">{p.sector} · {p.location} · objectif {money(p.funding_goal)}</p></div>
                    <Button size="sm" onClick={() => publish(p.id)} data-testid={`publish-${p.id}`} className="rounded-full bg-congo-blue">Publier</Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="users" className="pt-6">
            <h2 className="font-head text-lg font-semibold mb-4">Gestion des utilisateurs</h2>
            <div className="space-y-2" data-testid="users-list">
              {users.map((u) => (
                <div key={u.user_id} className="bg-white border rounded-xl p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0"><p className="font-medium truncate">{u.name || u.email}</p><p className="text-xs text-slate-500 truncate">{u.email}</p></div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-congo-bluelight text-congo-blue border-0 capitalize">{u.role}</Badge>
                    <Badge className={`border-0 ${u.kyc_status === "approved" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{u.kyc_status}</Badge>
                    {u.role === "investor" && (
                      <Button size="sm" variant="outline" onClick={() => suspend(u.user_id, !u.suspended)} data-testid={`suspend-${u.user_id}`} className={`rounded-full text-xs ${u.suspended ? "text-green-600" : "text-congo-red"}`}>{u.suspended ? "Réactiver" : "Suspendre"}</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="fees" className="pt-6">
            <h2 className="font-head text-lg font-semibold mb-4">Paramétrage des frais Studeur</h2>
            {fees && (
              <div className="bg-white border rounded-2xl p-6 max-w-md space-y-4">
                <div><Label>Frais d'entrée (% de l'investissement)</Label><Input type="number" step="0.1" value={fees.entry_fee_pct} onChange={(e) => setFees({ ...fees, entry_fee_pct: e.target.value })} data-testid="fee-entry" className="h-11 mt-1" /></div>
                <div><Label>Frais de gestion annuels (% des encours)</Label><Input type="number" step="0.1" value={fees.mgmt_fee_pct} onChange={(e) => setFees({ ...fees, mgmt_fee_pct: e.target.value })} data-testid="fee-mgmt" className="h-11 mt-1" /></div>
                <div><Label>Commission sur intérêts (% du coupon)</Label><Input type="number" step="0.1" value={fees.interest_commission_pct} onChange={(e) => setFees({ ...fees, interest_commission_pct: e.target.value })} data-testid="fee-commission" className="h-11 mt-1" /></div>
                <Button onClick={saveFees} data-testid="save-fees-btn" className="rounded-full bg-congo-blue">Enregistrer</Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="compliance" className="pt-6 space-y-8">
            <div>
              <h2 className="font-head text-lg font-semibold mb-1 flex items-center gap-2"><ClipboardList className="h-5 w-5 text-congo-blue" /> Évaluation des investisseurs</h2>
              <p className="text-sm text-slate-500 mb-4">Traçabilité : profil (versionné), test de connaissances, tentatives, consentements et indicateurs Compliance.</p>
              {evalOverview.length === 0 ? <p className="text-slate-500 text-sm" data-testid="no-eval">Aucun investisseur évalué pour le moment.</p> : (
                <div className="space-y-3" data-testid="eval-overview-list">
                  {evalOverview.map((e) => (
                    <div key={e.user_id} className="bg-white border rounded-xl p-4" data-testid={`eval-row-${e.user_id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0"><p className="font-medium truncate">{e.name || e.email}</p><p className="text-xs text-slate-500 truncate">{e.email}</p></div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`border-0 ${e.profile_version ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>Profil {e.profile_version ? `v${e.profile_version} (${e.profile_versions})` : "—"}</Badge>
                          <Badge className={`border-0 ${e.knowledge_passed ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>Test {e.knowledge_passed ? `réussi ${e.knowledge_score ?? ""}` : "—"}</Badge>
                          <Badge className="bg-slate-100 text-slate-600 border-0">{e.attempts} tentative(s)</Badge>
                          <Badge className={`border-0 ${e.consents_ok ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>Consentements {e.consents_ok ? "OK" : "—"}</Badge>
                        </div>
                      </div>
                      {e.flags && e.flags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {e.flags.map((f) => <Badge key={f.code} className="bg-amber-100 text-amber-800 border-0 text-[11px]">⚠ {f.label}</Badge>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="font-head text-lg font-semibold mb-1 flex items-center gap-2"><Brain className="h-5 w-5 text-congo-blue" /> Banque de questions ({evalQuestions.length})</h2>
              <p className="text-sm text-slate-500 mb-4">Activez ou désactivez une question sans redéploiement. Les questions inactives ne sont plus tirées.</p>
              <div className="space-y-2" data-testid="eval-questions-list">
                {evalQuestions.map((q) => (
                  <div key={q.id} className="bg-white border rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`eval-question-${q.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800"><span className="font-mono text-xs text-slate-400 mr-1">{q.id}</span>{q.text}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{q.category_label}{q.critical && <span className="text-congo-red font-semibold"> · critique</span>}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={`border-0 ${q.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{q.active ? "Active" : "Inactive"}</Badge>
                      <Button size="sm" variant="outline" onClick={() => toggleQuestion(q.id, !q.active)} data-testid={`toggle-question-${q.id}`} className={`rounded-full text-xs ${q.active ? "text-congo-red" : "text-green-600"}`}><Power className="h-3.5 w-3.5 mr-1" />{q.active ? "Désactiver" : "Activer"}</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
